import { MEDALS, medalFor } from '../game/tuning';

export function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, '0')}`;
}

export function ordinal(n: number): string {
  const suffix = n % 10 === 1 && n % 100 !== 11 ? 'st' : n % 10 === 2 && n % 100 !== 12 ? 'nd' : n % 10 === 3 && n % 100 !== 13 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

export class Hud {
  private readonly timer = this.get('#timer-value');
  private readonly best = this.get('#best-value');
  private readonly progressDuck = this.get('#progress-duck');
  private readonly progressGhost = this.get('#progress-ghost');
  private readonly banner = this.get('#banner');
  private readonly results = this.get('#results');
  private readonly resultTime = this.get('#result-time');
  private readonly resultNote = this.get('#result-note');
  private readonly resultCrumbs = this.get('#result-crumbs');
  private readonly resultCoins = this.get('#result-coins');
  private readonly titleCoins = this.get('#title-coins');
  private readonly resultMedal = this.get('#result-medal');

  constructor() {
    this.get('#pause-medals').textContent = MEDALS.map((m) => `${m.icon} ${formatTime(m.time).replace(/\.00$/, '')}`).join(' · ');
  }
  private readonly hint = this.get('#touch-hint');
  private readonly pauseOverlay = this.get('#pause-overlay');
  private readonly pips = Array.from(document.querySelectorAll<HTMLElement>('.paddle-pip'));
  private readonly paddleButton = this.get('#paddle-button');
  private readonly paddleWord = this.get('#paddle-word');
  private flapShown = false;
  private readonly streakDots = Array.from(document.querySelectorAll<HTMLElement>('.crumb-streak i'));
  private shownStreak = -1;
  private readonly coachBox = this.get('#coach');
  private readonly steerPad = this.get('#steer-pad');
  private readonly steerThumb = this.steerPad.querySelector<HTMLElement>('.steer-thumb');
  private coachTimer = 0;
  private readonly popup = this.get('#popup');
  private popupAnimation: Animation | null = null;
  private lastBanner = '';
  private readonly placeValue = this.get('#place-value');
  private readonly itemButton = this.get('#item-button');
  private readonly itemIcon = this.get('#item-icon');
  private shownItem: string | null = 'none';
  private readonly resultPlace = this.get('#result-place');
  private rivalMarkers: HTMLElement[] = [];

  /** One coloured dot per rival on the progress bar. */
  setRivals(colors: string[]): void {
    const track = this.get('#progress');
    for (const marker of this.rivalMarkers) marker.remove();
    this.rivalMarkers = colors.map((color) => {
      const marker = document.createElement('div');
      marker.className = 'progress-marker progress-rival';
      marker.style.background = color;
      track.insertBefore(marker, this.progressDuck);
      return marker;
    });
  }

  update(
    raceTime: number,
    bestTime: number | null,
    progress: number,
    ghostProgress: number | null,
    rivalProgress: number[] = [],
    place = 1,
    racers = 1,
  ): void {
    rivalProgress.forEach((value, i) => {
      const marker = this.rivalMarkers[i];
      if (marker) marker.style.left = `${Math.min(1, Math.max(0, value)) * 100}%`;
    });
    this.placeValue.textContent = `${ordinal(place)}/${racers}`;
    this.timer.textContent = formatTime(raceTime);
    const bestMedal = medalFor(bestTime);
    this.best.textContent = bestTime === null ? '—' : `${bestMedal ? `${bestMedal.icon} ` : ''}${formatTime(bestTime)}`;
    this.progressDuck.style.left = `${Math.min(1, Math.max(0, progress)) * 100}%`;
    this.progressGhost.hidden = ghostProgress === null;
    if (ghostProgress !== null) this.progressGhost.style.left = `${Math.min(1, Math.max(0, ghostProgress)) * 100}%`;
  }

  /** charges may be fractional: the next pip fills as it recharges. */
  setPaddles(charges: number): void {
    this.pips.forEach((pip, i) => {
      const fill = Math.max(0, Math.min(1, charges - i));
      pip.style.setProperty('--fill', fill.toFixed(3));
      pip.classList.toggle('is-ready', fill >= 1);
    });
  }

  /** Show whether a press now is a free flap (stalled) or spends a charge. */
  setFlap(flap: boolean): void {
    if (flap === this.flapShown) return;
    this.flapShown = flap;
    this.paddleButton.classList.toggle('is-flap', flap);
    this.paddleWord.textContent = flap ? 'Flap' : 'Paddle';
  }

  /** Show the drag-steer ring under the thumb (viewport px), or hide it. */
  setDrag(drag: { anchorX: number; y: number; steer: number } | null): void {
    this.steerPad.hidden = drag === null;
    if (!drag) return;
    const parent = this.steerPad.offsetParent?.getBoundingClientRect() ?? { left: 0, top: 0 };
    this.steerPad.style.left = `${drag.anchorX - parent.left}px`;
    this.steerPad.style.top = `${drag.y - parent.top}px`;
    if (this.steerThumb) this.steerThumb.style.transform = `translateX(${drag.steer * 40}px)`;
  }

  /** Crumb streak progress toward a free paddle (0..5 dots lit). */
  setStreak(count: number): void {
    if (count === this.shownStreak) return;
    this.shownStreak = count;
    this.streakDots.forEach((dot, i) => dot.classList.toggle('is-lit', i < count));
  }

  /**
   * A crumb flies from the duck (viewport fractions) into the paddle pip it's
   * filling, so it's obvious that crumbs are what charge your paddles. `big`
   * is the first-time version: larger, slower, and the button lights up.
   */
  crumbFly(fx: number, fy: number, charges: number, big = false): void {
    // A full bar has nothing to fill: the crumb goes to the streak meter instead.
    const full = charges >= this.pips.length;
    const pip = full ? this.streakDots[Math.max(0, Math.min(this.streakDots.length - 1, this.shownStreak))] : this.pips[Math.floor(charges)];
    if (!pip) return;
    const crumb = document.createElement('span');
    crumb.className = big ? 'crumb-fly is-big' : 'crumb-fly';
    this.fly(crumb, fx, fy, pip, big ? 1100 : 520, () => {
      pip.animate([{ transform: 'scale(1.3)' }, { transform: 'scale(1)' }], { duration: 160, easing: 'ease-out' });
      if (big) this.glow(this.paddleButton);
    });
  }

  /** The picked-up item flies from its box into the item button, which then shows it. */
  itemFly(fx: number, fy: number, icon: string, big: boolean, arrived: () => void): void {
    const token = document.createElement('span');
    token.className = big ? 'item-fly is-big' : 'item-fly';
    token.textContent = icon;
    this.fly(token, fx, fy, this.itemButton, big ? 1200 : 600, () => {
      arrived();
      this.glow(this.itemButton, big ? 3 : 1);
    });
  }

  /**
   * A short teaching callout above one of the action buttons. Hides itself
   * after a few seconds, or early via `hideCoach`.
   */
  coach(target: 'paddle' | 'item', html: string): void {
    const button = target === 'paddle' ? this.paddleButton : this.itemButton;
    this.showHint(false);
    this.coachBox.innerHTML = html;
    this.coachBox.dataset.target = target;
    this.coachBox.hidden = false;
    const bar = button.getBoundingClientRect();
    const parent = this.coachBox.offsetParent?.getBoundingClientRect() ?? { left: 0, top: 0 };
    this.coachBox.style.left = `${bar.left + bar.width / 2 - parent.left}px`;
    this.coachBox.style.top = `${bar.top - parent.top - 14}px`;
    this.coachBox.animate(
      [
        { transform: 'translate(-50%, -100%) translateY(10px) scale(0.8)', opacity: 0 },
        { transform: 'translate(-50%, -100%) scale(1)', opacity: 1 },
      ],
      { duration: 260, easing: 'cubic-bezier(0.2, 1.4, 0.5, 1)', fill: 'both' },
    );
    window.clearTimeout(this.coachTimer);
    this.coachTimer = window.setTimeout(() => this.hideCoach(), 4200);
  }

  get coachingItem(): boolean {
    return !this.coachBox.hidden && this.coachBox.dataset.target === 'item';
  }

  hideCoach(): void {
    window.clearTimeout(this.coachTimer);
    this.coachBox.hidden = true;
  }

  private fly(el: HTMLElement, fx: number, fy: number, target: HTMLElement, duration: number, done: () => void): void {
    const to = target.getBoundingClientRect();
    const fromX = fx * window.innerWidth;
    const fromY = fy * window.innerHeight;
    document.body.append(el);
    const dx = to.left + to.width / 2 - fromX;
    const dy = to.top + to.height / 2 - fromY;
    el.animate(
      [
        { transform: `translate(${fromX}px, ${fromY}px) translate(-50%, -50%) scale(0.6)`, opacity: 0.4 },
        { transform: `translate(${fromX + dx * 0.25}px, ${fromY - 40}px) translate(-50%, -50%) scale(1.25)`, opacity: 1, offset: 0.3 },
        { transform: `translate(${fromX + dx}px, ${fromY + dy}px) translate(-50%, -50%) scale(0.5)`, opacity: 0.9 },
      ],
      { duration, easing: 'cubic-bezier(0.5, 0, 0.8, 0.6)' },
    )
      .finished.then(() => {
        el.remove();
        done();
      })
      .catch(() => el.remove());
  }

  /** A warm pulse around a button: "this just got filled". */
  private glow(button: HTMLElement, times = 3): void {
    button.animate(
      [
        { boxShadow: '0 3px 0 rgba(22, 50, 58, 0.2), 0 0 0 0 rgba(255, 210, 31, 0.9)', transform: 'scale(1)' },
        { boxShadow: '0 3px 0 rgba(22, 50, 58, 0.2), 0 0 0 14px rgba(255, 210, 31, 0)', transform: 'scale(1.12)' },
        { boxShadow: '0 3px 0 rgba(22, 50, 58, 0.2), 0 0 0 0 rgba(255, 210, 31, 0)', transform: 'scale(1)' },
      ],
      { duration: 650, iterations: times, easing: 'ease-out' },
    );
  }

  flashPaddles(): void {
    for (const pip of this.pips) {
      pip.animate([{ transform: 'scale(1.35)' }, { transform: 'scale(1)' }], { duration: 220, easing: 'ease-out' });
    }
  }

  /** Short floating callout above the duck ("SURF!", "Close shave!"). */
  pop(text: string, tone: 'surf' | 'shave'): void {
    this.popup.textContent = text;
    this.popup.dataset.tone = tone;
    this.popup.hidden = false;
    this.popupAnimation?.cancel();
    this.popupAnimation = this.popup.animate(
      [
        { transform: 'translate(-50%, 0) scale(0.6)', opacity: 0 },
        { transform: 'translate(-50%, -14px) scale(1.1)', opacity: 1, offset: 0.2 },
        { transform: 'translate(-50%, -30px) scale(1)', opacity: 1, offset: 0.7 },
        { transform: 'translate(-50%, -44px) scale(1)', opacity: 0 },
      ],
      { duration: 900, easing: 'ease-out' },
    );
    this.popupAnimation.onfinish = () => {
      this.popup.hidden = true;
    };
  }

  showBanner(text: string): void {
    if (text === this.lastBanner) return;
    this.lastBanner = text;
    this.banner.textContent = text;
    this.banner.hidden = text === '';
    if (text) {
      this.banner.animate(
        [
          { transform: 'translate(-50%, -50%) scale(1.5)', opacity: 0 },
          { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
        ],
        { duration: 260, easing: 'cubic-bezier(.2,1.4,.4,1)' },
      );
    }
  }

  /** The held power-up (icon), or null for an empty slot. */
  setItem(icon: string | null): void {
    if (icon === this.shownItem) return;
    this.shownItem = icon;
    this.itemIcon.textContent = icon ?? '';
    this.itemButton.classList.toggle('is-empty', icon === null);
    if (icon) {
      this.itemButton.animate([{ transform: 'scale(1.3)' }, { transform: 'scale(1)' }], { duration: 260, easing: 'ease-out' });
    }
  }

  setCoins(coins: number): void {
    this.titleCoins.textContent = String(coins);
  }

  showTitle(visible: boolean): void {
    this.get('#title-screen').hidden = !visible;
  }

  showPaused(paused: boolean): void {
    this.pauseOverlay.hidden = !paused;
  }

  showHint(visible: boolean): void {
    this.hint.hidden = !visible;
  }

  showResults(
    time: number,
    previousBest: number | null,
    isBest: boolean,
    crumbs: number,
    crumbTotal: number,
    place: number,
    racers: number,
    coins: { lines: { label: string; coins: number }[]; total: number } | null = null,
    practice = false,
  ): void {
    this.resultPlace.textContent = place === 1 ? '1st place! 🏆' : `${ordinal(place)} of ${racers}`;
    this.resultPlace.classList.toggle('is-winner', place === 1);
    this.resultTime.textContent = formatTime(time);
    // Medal earned, and how far off the next one up.
    const medal = medalFor(time);
    const next = medal ? MEDALS[MEDALS.indexOf(medal) - 1] : MEDALS[MEDALS.length - 1];
    const earned = medal ? `${medal.icon} ${medal.name}!` : 'No medal yet';
    const chase = next ? ` · ${next.icon} ${next.name} is ${formatTime(next.time)} (${(time - next.time).toFixed(2)}s to find)` : '';
    this.resultMedal.textContent = earned + chase;
    this.resultCrumbs.textContent = `${crumbs} / ${crumbTotal} crumbs`;
    this.resultCoins.hidden = coins === null;
    if (coins) {
      const earned = coins.lines.reduce((sum, line) => sum + line.coins, 0);
      const parts = coins.lines.map((line) => `${line.label} +${line.coins}`).join(' · ');
      this.resultCoins.innerHTML = `<strong><i class="coin"></i> +${earned}</strong> <small>(${parts})</small><br>${coins.total} coins · spend them on ducks`;
    }
    if (previousBest === null) this.resultNote.textContent = 'First run — your ghost will race you next time';
    else if (isBest) this.resultNote.textContent = `New best! ${(previousBest - time).toFixed(2)}s faster`;
    else this.resultNote.textContent = `+${(time - previousBest).toFixed(2)}s behind your best`;
    if (practice) {
      this.resultMedal.textContent = 'Practice run (started part-way)';
      this.resultNote.textContent = 'No medal, best time or coins: start from the top to count';
    }
    this.results.classList.toggle('is-best', isBest);
    this.results.hidden = false;
  }

  hideResults(): void {
    this.results.hidden = true;
  }

  private get(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}
